import { Icon } from "@iconify/react"
import { Button, Card, Flex, Grid, Input, message, Pagination, Table, Tag } from "antd"
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { testimonyLaboratoryService } from "../../../services/testimony-laboratory.service";
import useTitleStore from "../../../store/titleStore";
import { useTableHeight } from "../../../hooks/useTableHeight";
import useDebounce from "../../../hooks/useDebounce";
import TableAction from "../../../components/TableAction";
import UpdatedByCell from "../../../components/UpdatedByCell";

export default function TestimonyLaboratoryIndex(){

  const breakpoint = Grid.useBreakpoint()

  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight();
  
  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: []
  })

  const { data: testimonyData, isLoading, isRefetching } = useQuery({
    queryKey: ['testimonyLaboratory', dataParams],
    queryFn: () => testimonyLaboratoryService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => testimonyLaboratoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries('testimonyLaboratory');
      messageApi.success('Data berhasil dihapus');
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan saat menghapus data');
    }
  })

  useEffect(() => {
    setTitle("Testimoni Laboratorium");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      filter: [
        {
          field: 'name',
          operator: 'like',
          value: debouncedKeyword
        }
      ]
    }));
  }, [debouncedKeyword]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      render: (text, record, index) =>  ((testimonyData?.currentPage - 1) * dataParams.limit) + index + 1,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Asal',
      dataIndex: 'origin',
      width: 150,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Nama',
      dataIndex: 'name',
      width: 180,
    },
    {
      title: 'Testimoni',
      dataIndex: 'content',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 120,
      render: val => val ? <Tag color="green">Aktif</Tag> : <Tag color="red">Nonaktif</Tag>
    },
    {
      title: 'Tanggal Update',
      width: 180,
      render: val => <UpdatedByCell value={val} />,
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <TableAction
          onClickEdit={() => navigate('/lab-service/testimony/detail/'+record.id, { state: { testimony: record } })}
          handleDelete={() => handleDelete(record.id)}
          deleteLoading={deleteLoading}
        />
      )
    },
  ]

  return (
    <Card
      styles={{
        body: {
          paddingLeft: 0,
          paddingRight: 0
        }
      }}
    >
      {contextHolder}
      <Flex justify="space-between" gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
        <Input 
          style={{ width: 320 }} 
          prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
          placeholder="cari nama..."
          onChange={e => setKeyword(e.target.value)}
          value={keyword}
        />
        <Button 
          variant="solid" 
          color="primary"  
          icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
          onClick={() => navigate('/lab-service/testimony/create')}
          style={{ minWidth: 38 }}
        >
          {breakpoint.md && 'Tambah Testimoni'}
        </Button>
      </Flex>
      <Table
        columns={columns}
        pagination={false}
        size="small"
        loading={isLoading || isRefetching}
        rowKey={'id'}
        scroll={{ y: tableHeight, x: 1100 }}
        dataSource={testimonyData?.items}
      />
       <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={testimonyData?.totalItems}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={['10', '20', '50']}
          current={testimonyData?.currentPage}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
    </Card>
  )
}